import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, KeyboardEvent, MouseEvent, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconThreeDots } from '~/ui/core/icons';

import { useJobArchitecturePermissions } from '../../context/JobArchitecturePermissionsContext';
import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import { CommentMessage } from './CommentMessage';
import { CommentReplyComposer } from './CommentReplyComposer';
import type { Comment } from './types';
import { useActivateThread } from './use-activate-thread';
import { useCreateReply } from './use-create-reply';
import { useCurrentCommentAuthor } from './use-current-comment-author';
import { useDeleteComment } from './use-delete-comment';
import { useRepliesQuery } from './use-replies-query';
import { useResolveComment } from './use-resolve-comment';

type RegisterCard = (id: string, el: HTMLElement | null) => void;

type CommentThreadCardProps = {
    readonly architectureId: string;
    readonly comment: Comment;
    readonly commentResolutionDisabled?: boolean;
    readonly itemId: string;
    readonly language: string;
    readonly registerCard: RegisterCard;
    readonly version: number;
};

const sortByCreatedAtAscending = (a: Comment, b: Comment): number => a.createdAt.localeCompare(b.createdAt);

const useFocusReplyComposer = (isActive: boolean, replyInputRef: RefObject<HTMLTextAreaElement | null>): void => {
    useEffect(() => {
        if (!isActive) return;
        const handle = requestAnimationFrame(() => {
            replyInputRef.current?.focus({ preventScroll: true });
        });

        return () => {
            cancelAnimationFrame(handle);
        };
    }, [isActive, replyInputRef]);
};

const ReplyList: FunctionComponent<{ readonly replies: readonly Comment[] }> = ({ replies }) =>
    replies.length > 0 ? (
        <div className="pt-s">
            <div className="flex flex-col gap-xxl" data-testid="comment-replies">
                {replies.map((reply) => (
                    <div key={reply.id} data-testid="comment-reply">
                        <CommentMessage author={reply.author} content={reply.content} createdAt={reply.createdAt} />
                    </div>
                ))}
            </div>
        </div>
    ) : null;

type ReplyComposerShellProps = {
    readonly createReplyPending: boolean;
    readonly inputRef: RefObject<HTMLTextAreaElement | null>;
    readonly isActive: boolean;
    readonly replyBody: string;
    readonly onCancel: () => void;
    readonly onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
    readonly onReplyBodyChange: (value: string) => void;
    readonly onSubmit: () => void;
};

const ReplyComposerShell: FunctionComponent<ReplyComposerShellProps> = ({
    createReplyPending,
    inputRef,
    isActive,
    onCancel,
    onKeyDown,
    onReplyBodyChange,
    onSubmit,
    replyBody,
}) => (
    <div
        aria-hidden={!isActive}
        className={isActive ? 'max-h-[calc(var(--spacing-20xl)*2)] pt-s opacity-100' : 'max-h-0 pt-0 opacity-0'}
        style={{ pointerEvents: isActive ? 'auto' : 'none' }}
    >
        <div>
            <CommentReplyComposer
                inputRef={inputRef}
                isActive={isActive}
                replyBody={replyBody}
                submitDisabled={createReplyPending}
                onCancel={onCancel}
                onKeyDown={onKeyDown}
                onReplyBodyChange={onReplyBodyChange}
                onSubmit={onSubmit}
            />
        </div>
    </div>
);

export const CommentThreadCard: FunctionComponent<CommentThreadCardProps> = ({
    architectureId,
    comment,
    commentResolutionDisabled = false,
    itemId,
    language,
    registerCard,
    version,
}) => {
    const translate = useTranslate();
    const { canRead } = useJobArchitecturePermissions();
    const currentAuthor = useCurrentCommentAuthor();
    const activateThread = useActivateThread(itemId);
    const isActive = useActiveThreadStore((state) => state.activeThreadId === comment.id);
    const setActiveThreadId = useActiveThreadStore((state) => state.setActiveThreadId);
    const createReply = useCreateReply({
        architectureId,
        itemId,
        language,
        parentCommentId: comment.id,
        version,
    });
    const deleteComment = useDeleteComment({ architectureId, itemId, language, version });
    const resolveComment = useResolveComment({ architectureId, itemId, language, version });
    const [replyBody, setReplyBody] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const replyInputRef = useRef<HTMLTextAreaElement>(null);
    useFocusReplyComposer(isActive, replyInputRef);
    const { data: replies } = useRepliesQuery({
        architectureId,
        itemId,
        parentCommentId: comment.id,
        version,
    });

    const handleRef = useCallback(
        (el: HTMLElement | null) => {
            registerCard(comment.id, el);
        },
        [registerCard, comment.id],
    );

    const cardBaseClasses = 'absolute left-0 right-0 m-0 min-w-0 border-0 bg-transparent p-0 px-s pb-s';
    const sortedReplies = [...(replies ?? [])].sort(sortByCreatedAtAscending);
    const showRepliesBlock = sortedReplies.length > 0 || isActive;
    const canDelete = comment.author.id === currentAuthor.id;
    const canResolve = canRead && is.nullOrUndefined(comment.resolution);
    const hasThreadActions = canDelete || canResolve;

    useEffect(() => {
        if (isActive) return;

        const timeout = window.setTimeout(() => {
            setReplyBody('');
        }, 220);

        return () => {
            window.clearTimeout(timeout);
        };
    }, [isActive]);

    const handleCardClick = (event: MouseEvent<HTMLElement>): void => {
        const target = event.target instanceof HTMLElement ? event.target : null;
        if (is.null(target)) return;
        if (!is.null(target.closest('button')) || !is.null(target.closest('textarea'))) return;
        activateThread(comment.id, comment.target);
    };

    const handleReplySubmit = (): void => {
        const trimmedReply = replyBody.trim();
        if (trimmedReply.length === 0 || createReply.isPending) return;
        const replyId = crypto.randomUUID();

        createReply.mutate({
            content: trimmedReply,
            id: replyId,
            parentTarget: comment.target,
        });
        setReplyBody('');
        replyInputRef.current?.focus({ preventScroll: true });
    };

    const handleDelete = (): void => {
        setConfirmOpen(false);
        if (deleteComment.isPending) return;
        if (isActive) {
            setActiveThreadId(null);
        }
        deleteComment.mutate({ commentId: comment.id });
    };

    const handleResolve = (): void => {
        if (commentResolutionDisabled || resolveComment.isPending) return;
        if (isActive) {
            setActiveThreadId(null);
        }
        resolveComment.mutate({ commentId: comment.id });
    };

    const handleReplyKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleReplySubmit();
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            setReplyBody('');
        }
    };

    const cardClasses = clsx(
        'group/comment relative cursor-pointer overflow-hidden rounded-[calc(var(--radius-md)+var(--spacing-s))] bg-white-200',
        'transition-shadow duration-200 ease-[cubic-bezier(.165,.84,.44,1)]',
        isActive ? 'shadow-lg hover:shadow-lg' : 'shadow-sm hover:shadow-md',
    );

    return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: card-level activation delegation; controls inside are keyboard-accessible
        // biome-ignore lint/a11y/noStaticElementInteractions: card surface acts as a delegating activation target; inner buttons are keyboard-accessible
        <div
            ref={handleRef}
            className={cardBaseClasses}
            data-comment-id={comment.id}
            data-comment-active={isActive}
            data-target-kind={comment.target.kind}
            data-testid="comment-thread-card"
            onClick={handleCardClick}
        >
            <div className={cardClasses}>
                <div className="flex flex-col p-xl">
                    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-s">
                        <div className="min-w-0">
                            <CommentMessage
                                author={comment.author}
                                content={comment.content}
                                createdAt={comment.createdAt}
                            />
                        </div>
                        {hasThreadActions ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger>
                                    <Button
                                        aria-label={translate('common.more')}
                                        className="pointer-events-none opacity-0 transition-opacity duration-150 ease-out group-hover/comment:pointer-events-auto group-hover/comment:opacity-100 group-focus-within/comment:pointer-events-auto group-focus-within/comment:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
                                        iconOnly={true}
                                        size="sm"
                                        type="button"
                                        variant="tertiary"
                                    >
                                        <IconThreeDots />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                                    {canResolve ? (
                                        <DropdownMenuItem
                                            data-testid="comment-thread-resolve"
                                            disabled={commentResolutionDisabled || resolveComment.isPending}
                                            onSelect={handleResolve}
                                            tooltipContent={
                                                commentResolutionDisabled
                                                    ? translate(
                                                          'pages.job-architecture-v2.comments.thread.resolve-disabled-edit-mode',
                                                      )
                                                    : undefined
                                            }
                                        >
                                            {translate('pages.job-architecture-v2.comments.thread.resolve')}
                                        </DropdownMenuItem>
                                    ) : null}
                                    {canDelete ? (
                                        <DropdownMenuItem
                                            className="hover:!text-error-600"
                                            data-testid="comment-thread-delete"
                                            disabled={deleteComment.isPending}
                                            onSelect={() => setConfirmOpen(true)}
                                        >
                                            {translate('pages.job-architecture-v2.comments.thread.delete')}
                                        </DropdownMenuItem>
                                    ) : null}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : null}
                        <ConfirmationDialog
                            cancelButtonLabel={translate('common.cancel')}
                            confirmButtonLabel={translate('common.delete')}
                            confirmButtonVariant="destructive"
                            description={translate(
                                'pages.job-architecture-v2.comments.thread.delete-confirm-description',
                            )}
                            open={confirmOpen}
                            title={translate('pages.job-architecture-v2.comments.thread.delete-confirm-title')}
                            onCancel={() => setConfirmOpen(false)}
                            onConfirm={handleDelete}
                        />
                    </div>
                    <div
                        aria-hidden={!showRepliesBlock}
                        className={clsx(
                            'transition-[max-height,opacity] duration-200 ease-[cubic-bezier(.165,.84,.44,1)]',
                            showRepliesBlock ? 'max-h-[calc(var(--spacing-20xl)*6)] opacity-100' : 'max-h-0 opacity-0',
                        )}
                    >
                        <div>
                            <div className="pt-s">
                                <div className="flex flex-col border-t border-gray-200/80">
                                    <ReplyList replies={sortedReplies} />
                                    <ReplyComposerShell
                                        createReplyPending={createReply.isPending}
                                        inputRef={replyInputRef}
                                        isActive={isActive}
                                        replyBody={replyBody}
                                        onCancel={() => {
                                            setActiveThreadId(null);
                                        }}
                                        onKeyDown={handleReplyKeyDown}
                                        onReplyBodyChange={setReplyBody}
                                        onSubmit={handleReplySubmit}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
